// src/components/Puzzle/parts/Root.tsx
import React, { useContext, useEffect, useReducer } from "react";
import { Chessboard } from "react-chessboard";
import { parse } from "@mliebelt/pgn-parser";
import { Chess } from "chess.js";

var FAIL_COLOR = "rgba(201, 52, 48, 0.5)";
var SUCCESS_COLOR = "rgba121, 143, 208, 0.5)";
var HINT_COLOR = "rgba(66, 104, 218, 0.5)";
var CPU_MOVE_COLOR = "rgba(121, 143, 208, 0.5)";
var getCustomSquareStyles = (
  status,
  hint,
  isPlayerTurn,
  activeSquare,
  nextMove,
  lastMove,
) => {
  const customSquareStyles = {};
  if (status === "failed" && lastMove) {
    customSquareStyles[lastMove.from] = {
      backgroundColor: FAIL_COLOR,
    };
    customSquareStyles[lastMove.to] = {
      backgroundColor: FAIL_COLOR,
    };
  }
  if (
    lastMove &&
    (status === "solved" || (status !== "failed" && !isPlayerTurn))
  ) {
    customSquareStyles[lastMove.from] = {
      backgroundColor: SUCCESS_COLOR,
    };
    customSquareStyles[lastMove.to] = {
      backgroundColor: SUCCESS_COLOR,
    };
  }
  if (lastMove && isPlayerTurn) {
    customSquareStyles[lastMove.from] = {
      backgroundColor: CPU_MOVE_COLOR,
    };
    customSquareStyles[lastMove.to] = {
      backgroundColor: CPU_MOVE_COLOR,
    };
  }
  if (activeSquare) {
    customSquareStyles[activeSquare] = {
      backgroundColor: CPU_MOVE_COLOR,
    };
  }
  if (hint === "piece") {
    if (nextMove) {
      customSquareStyles[nextMove.from] = {
        backgroundColor: HINT_COLOR,
      };
    }
  }
  if (hint === "move") {
    if (nextMove) {
      customSquareStyles[nextMove.from] = {
        backgroundColor: HINT_COLOR,
      };
      customSquareStyles[nextMove.to] = {
        backgroundColor: HINT_COLOR,
      };
    }
  }
  return customSquareStyles;
};
var getOrientation = (puzzle) => {
  const fen = puzzle.fen;
  const game = new Chess(fen);
  if (puzzle.makeFirstMove) {
    game.move(puzzle.moves[0]);
  }
  return game.turn() === "w" ? "white" : "black";
};
var isClickableElement = (element) => React.isValidElement(element);
var getMove = (game, move) => {
  const copy = new Chess(game.fen());
  return copy.move(move);
};
var isLegalMove = (game, move) => {
  const copy = new Chess(game.fen());
  try {
    copy.move(move);
    return true;
  } catch (e) {
    return false;
  }
};
var requiresPromotion = (game, move) => {
  const copy = new Chess(game.fen());
  const result = copy.move(move);
  if (result === null) {
    return false;
  }
  return result.flags.indexOf("p") !== -1;
};

// src/components/Puzzle/hooks/reducer.ts
var initializeGame = (puzzle) => {
  const game = new Chess(puzzle.fen);
  return {
    puzzle,
    currentMoveIndex: 0,
    game,
    orientation: getOrientation(puzzle),
    status: "not-started",
    lastMove: game.history({ verbose: true })[game.history().length - 1],
    nextMove: getMove(game, puzzle.moves[0]),
    hint: "none",
    needCpuMove: !!puzzle.makeFirstMove,
    isPlayerTurn: !puzzle.makeFirstMove,
  };
};
var reducer = (state, action) => {
  var _a;
  switch (action.type) {
    case "INITIALIZE":
      return {
        ...state,
        ...initializeGame(action.payload),
      };
    case "RESET":
      return {
        ...state,
        ...initializeGame(state.puzzle),
      };
    case "TOGGLE_HINT":
      if (state.hint === "none") {
        return { ...state, hint: "piece" };
      }
      return { ...state, hint: "move" };
    case "CPU_MOVE":
      if (state.isPlayerTurn) {
        return state;
      }
      if (["solved", "failed"].includes(state.status)) {
        return state;
      }
      const gameWithCpuMove = new Chess(state.game.fen());
      if (state.nextMove) {
        gameWithCpuMove.move(state.nextMove);
      }
      return {
        ...state,
        game: gameWithCpuMove,
        currentMoveIndex: state.currentMoveIndex + 1,
        lastMove: gameWithCpuMove.history({ verbose: true })[
          gameWithCpuMove.history().length - 1
        ],
        nextMove:
          state.currentMoveIndex < state.puzzle.moves.length - 1
            ? getMove(
                gameWithCpuMove,
                state.puzzle.moves[state.currentMoveIndex + 1],
              )
            : null,
        needCpuMove: false,
        isPlayerTurn: true,
        status: "in-progress",
      };
    case "PLAYER_MOVE":
      const {
        sourceSquare,
        targetSquare,
        piece,
        onSolve,
        onFail,
        changePuzzle,
      } = action.payload;
      if (["solved", "failed"].includes(state.status)) {
        return state;
      }
      let gameWithMove = new Chess(state.game.fen());
      let move = null;
      try {
        move = gameWithMove.move({
          from: sourceSquare,
          to: targetSquare,
          promotion:
            ((_a = piece == null ? void 0 : piece[1]) == null
              ? void 0
              : _a.toLowerCase()) ?? "q",
        });
      } catch (error) {
        return state;
      }
      let gameWithRightMove = new Chess(state.game.fen());
      if (state.nextMove) {
        gameWithRightMove.move(state.nextMove);
      }
      const isMoveRight = gameWithRightMove.fen() === gameWithMove.fen();
      const isPuzzleSolved =
        state.currentMoveIndex === state.puzzle.moves.length - 1;
      if (!isMoveRight) {
        if (onFail) {
          onFail(changePuzzle);
        }
        return {
          ...state,
          game: gameWithMove,
          status: "failed",
          lastMove: move,
          nextMove: null,
          hint: "none",
          isPlayerTurn: false,
        };
      }
      if (isPuzzleSolved) {
        if (onSolve) {
          onSolve(changePuzzle);
        }
        return {
          ...state,
          game: gameWithMove,
          status: "solved",
          lastMove: move,
          nextMove: null,
          hint: "none",
          isPlayerTurn: false,
        };
      }
      return {
        ...state,
        hint: "none",
        game: gameWithMove,
        currentMoveIndex: state.currentMoveIndex + 1,
        lastMove: gameWithMove.history({ verbose: true })[
          gameWithMove.history().length - 1
        ],
        nextMove: getMove(
          gameWithMove,
          state.puzzle.moves[state.currentMoveIndex + 1],
        ),
        status: "in-progress",
        needCpuMove: true,
        isPlayerTurn: false,
      };
    default:
      return state;
  }
};

// src/components/Puzzle/hooks/usePuzzle.ts
var usePuzzle = (puzzle, onSolve, onFail) => {
  const [state, dispatch] = useReducer(reducer, puzzle, initializeGame);
  useEffect(() => {
    if (state.needCpuMove) {
      setTimeout(
        () =>
          dispatch({
            type: "CPU_MOVE",
          }),
        300,
      );
    }
  }, [state.needCpuMove]);
  const changePuzzle = (puzzle2) => {
    dispatch({ type: "INITIALIZE", payload: puzzle2 });
  };
  function handlePieceDrop(sourceSquare, targetSquare, piece) {
    dispatch({
      type: "PLAYER_MOVE",
      payload: {
        sourceSquare,
        targetSquare,
        piece,
        onSolve,
        onFail,
        changePuzzle,
      },
    });
    return true;
  }
  const onHint = () => {
    dispatch({ type: "TOGGLE_HINT" });
  };
  return {
    game: state.game,
    orientation: state.orientation,
    status: state.status,
    lastMove: state.lastMove,
    handlePieceDrop,
    changePuzzle,
    puzzle,
    hint: state.hint,
    onHint,
    nextMove: state.nextMove,
    isPlayerTurn: state.isPlayerTurn,
  };
};
var usePuzzle_default = usePuzzle;

// src/components/Puzzle/parts/Root.tsx
var PuzzleContext = React.createContext(null);
var Root = ({ puzzle, onSolve, onFail, children }) => {
  const context = usePuzzle_default(puzzle, onSolve, onFail);
  return /* @__PURE__ */ React.createElement(
    PuzzleContext.Provider,
    { value: context },
    children,
  );
};

// src/components/Puzzle/parts/Board.tsx

var Board = ({ ...rest }) => {
  const puzzleContext = useContext(PuzzleContext);
  if (!puzzleContext) {
    throw new Error("PuzzleContext not found");
  }
  const [activeSquare, setActiveSquare] = React.useState(null);
  const [promotionMove, setPromotionMove] = React.useState(null);
  const onSquareClick = (square) => {
    if (["solved", "failed"].includes(status)) {
      return;
    }
    if (activeSquare === null) {
      return setActiveSquare(game.moves({ square }).length ? square : null);
    }
    if (!isLegalMove(game, `${activeSquare}${square}`)) {
      return setActiveSquare(game.moves({ square }).length ? square : null);
    }
    if (requiresPromotion(game, `${activeSquare}${square}`)) {
      setPromotionMove({
        from: activeSquare,
        to: square,
      });
      return console.log("requires promotion dialog");
    }
    handlePieceDrop(activeSquare, square);
    setActiveSquare(null);
  };
  const onPromotionPieceSelect = (piece) => {
    if (
      (promotionMove == null ? void 0 : promotionMove.from) &&
      (promotionMove == null ? void 0 : promotionMove.to) &&
      piece
    ) {
      setPromotionMove(null);
      return handlePieceDrop(
        promotionMove == null ? void 0 : promotionMove.from,
        promotionMove == null ? void 0 : promotionMove.to,
        piece,
      );
    }
    return false;
  };
  const {
    game,
    status,
    lastMove,
    nextMove,
    orientation,
    handlePieceDrop,
    hint,
    isPlayerTurn,
  } = puzzleContext;
  return /* @__PURE__ */ React.createElement(Chessboard, {
    customSquareStyles: getCustomSquareStyles(
      status,
      hint,
      isPlayerTurn,
      activeSquare,
      nextMove,
      lastMove,
    ),
    boardOrientation: orientation,
    position: game.fen(),
    showPromotionDialog: !!promotionMove,
    onPromotionPieceSelect: !!promotionMove ? onPromotionPieceSelect : void 0,
    onPieceDrop: (sourceSquare, targetSquare, piece) =>
      handlePieceDrop(sourceSquare, targetSquare, piece),
    onSquareClick,
    areArrowsAllowed: true,
    animationDuration: status === "not-started" ? 0 : 300,
    ...rest,
  });
};

// src/components/Puzzle/parts/Reset.tsx
var defaultShowOn = ["failed", "solved"];
var Reset = ({
  children,
  asChild,
  puzzle,
  onReset,
  showOn = defaultShowOn,
}) => {
  const puzzleContext = useContext(PuzzleContext);
  if (!puzzleContext) {
    throw new Error("PuzzleContext not found");
  }
  const { changePuzzle, status } = puzzleContext;
  const handleClick = () => {
    changePuzzle(puzzle || puzzleContext.puzzle);
    onReset == null ? void 0 : onReset();
  };
  if (!showOn.includes(status)) {
    return null;
  }
  if (asChild) {
    const child = React.Children.only(children);
    if (isClickableElement(child)) {
      return React.cloneElement(child, {
        onClick: handleClick,
      });
    } else {
      throw new Error("Change child must be a clickable element");
    }
  }
  return /* @__PURE__ */ React.createElement(
    "button",
    { type: "button", onClick: handleClick },
    children,
  );
};

// src/components/Puzzle/parts/Hint.tsx
var defaultShowOn2 = ["not-started", "in-progress"];
var Hint = ({ children, asChild, showOn = defaultShowOn2 }) => {
  const puzzleContext = useContext(PuzzleContext);
  if (!puzzleContext) {
    throw new Error("PuzzleContext not found");
  }
  const { onHint, status } = puzzleContext;
  const handleClick = () => {
    onHint();
  };
  if (!showOn.includes(status)) {
    return null;
  }
  if (asChild) {
    const child = React.Children.only(children);
    if (isClickableElement(child)) {
      return React.cloneElement(child, {
        onClick: handleClick,
      });
    } else {
      throw new Error("Change child must be a clickable element");
    }
  }
  return (
    <button
      className="puzzle-button-restart-casual"
      type="button"
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

// src/components/Puzzle/index.ts
var Puzzle = {
  Root,
  Board,
  Reset,
  Hint,
};

// src/components/utils/puzzle.ts
var fromPgn = (pgn) => {
  const parsed = parse(pgn, { startRule: "game" });
  if (!parsed) {
    throw new Error("No games found in PGN");
  }
  if (
    !("tags" in parsed) ||
    !(parsed == null ? void 0 : parsed.tags) ||
    !("FEN" in (parsed == null ? void 0 : parsed.tags)) ||
    !("moves" in parsed)
  ) {
    throw new Error("Invalid PGN");
  }
  const moves = parsed.moves.map((move) => move.notation.notation);
  return {
    fen: parsed.tags.FEN,
    moves,
  };
};
export { Puzzle, fromPgn };
//# sourceMappingURL=index.mjs.map
